import { FacebookF } from '@allocab/allocab-icons';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useState } from 'react';
import FacebookLogin, { ReactFacebookLoginInfo } from 'react-facebook-login';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';

import allocabLogo from '../assets/images/allocab-logo.svg';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { Spinner } from '../components/Spinner';
import { constants } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { queryErrorHandler } from '../services/datastore/react-query';
import { APIError } from '../services/network/types';
import { t } from '../services/translation/translation';

export const LoginRoute = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { signinMutation, googleSigninMutation, facebookSigninMutation } = useAuth();

  const responseFacebook = (response: ReactFacebookLoginInfo) => {
    if (response?.accessToken) {
      facebookSigninMutation.mutate(
        { token: response?.accessToken, email: response?.email },
        {
          onError: (e: APIError) => {
            if (e?.errorMessage === 'NO_USER_FOUND') {
              navigate('/subscribe', { state: { userFromFacebook: response } });
            } else {
              queryErrorHandler(e);
            }
          },
        },
      );
    }
  };
  const responseGoogle = (credentialResponse: { credential?: string; clientId?: string }) => {
    if (credentialResponse.credential && credentialResponse.clientId) {
      googleSigninMutation.mutate(
        {
          googleToken: credentialResponse?.credential,
          clientId: credentialResponse.clientId,
        },
        {
          onError: () => {
            navigate('/subscribe');
          },
        },
      );
    }
  };

  return (
    <div className="h-[calc(100vh_-_3rem)] overflow-y-auto">
      <Helmet>
        <title>{t('login-route.meta-title')}</title>
      </Helmet>
      <video
        className="hidden md:block fixed bottom-0 right-0 z-[-100] min-w-full min-h-full w-auto h-auto object-cover"
        preload="none"
        autoPlay
        loop
        muted
      >
        <source
          src="//storage.googleapis.com/allocab_prod_apps/webapp/media/stock-footage-out-of-focus-city-traffic-at-night-car-headlights-and-tail-lights-out-of-focus.mov"
          type="video/mp4"
        />
      </video>
      <div className="flex min-h-full flex-col justify-center pb-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-background p-4 shadow sm:px-10 md:opacity-90">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <img className="mx-auto h-16 w-auto" src={allocabLogo} alt="allocab" />
              <h2 className="my-6 text-center text-3xl font-bold tracking-tight ">
                {constants.isHotels ? t('login.hotels.welcome') : t('login.welcome')}
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium leading-6 ">
                  {t('login.email')}
                </label>
                <div className="mt-2">
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="jean@allocab.com"
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full  border-0 py-1.5  shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium leading-6 ">
                  {t('login.password')}
                </label>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="************"
                    autoComplete="current-password"
                    required
                    className="block w-full  border-0 py-1.5  shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <PrimaryButton
                  className="w-full"
                  onClick={() => signinMutation.mutate({ email, password })}
                  isLoading={signinMutation.isLoading}
                >
                  {t('login.login')}
                </PrimaryButton>
              </div>
            </div>
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-background px-2">{t('login.continue')}</span>
                </div>
              </div>

              <div className="flex flex-col w-full">
                <div className="flex justify-center mt-5">
                  {facebookSigninMutation.isLoading ? (
                    <div className="w-64 bg-facebook text-text_light font-bold py-2 px-4  flex justify-center items-center hover:opacity-80">
                      <Spinner />
                    </div>
                  ) : (
                    <FacebookLogin
                      appId={constants.facebookAuthCred}
                      autoLoad={false}
                      fields="last_name,email,first_name"
                      callback={(response: ReactFacebookLoginInfo) => {
                        if ('accessToken' in response) {
                          responseFacebook(response);
                        }
                      }}
                      cssClass={
                        'w-64 bg-facebook text-text_light font-bold py-2 px-4  flex justify-center items-center hover:opacity-80'
                      }
                      textButton={`${t('login.facebook')}`}
                      icon={<FacebookF className="mr-2 fill-text_light" width={16} height={16} />}
                    />
                  )}
                </div>

                <div className="flex justify-center mt-5">
                  {googleSigninMutation.isLoading ? (
                    <div className="w-64 border  bg-background font-bold py-2 px-4  flex justify-center items-center hover:opacity-80">
                      <Spinner />
                    </div>
                  ) : (
                    <GoogleOAuthProvider clientId={constants.googleAuthCred}>
                      <GoogleLogin
                        onSuccess={credentialResponse => {
                          responseGoogle(credentialResponse);
                        }}
                      />
                    </GoogleOAuthProvider>
                  )}
                </div>
              </div>
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t" />
                  </div>
                </div>
                <div className="flex justify-center">
                  <Link className="text-cta cursor-pointer mt-5" to={`/reset-password`}>
                    {t('login.forgotten-password')}
                  </Link>
                </div>
                <div className="flex justify-center mt-3">
                  <span className="bg-background px-2">{t('login.no-account')}</span>
                  <Link className="text-cta font-bold hover:opacity-80" to={`/subscribe`}>
                    {t('login.register')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
